import React from "react";
import {
    capitalize,
    formatDate,
    formatCharges,
    decodeHtml,
    stripHtml,
} from "../utils";

export default function SubscriptionDetails({
    selectedSubscriptionId,
    subscriptionDetails,
    loadingSubDetails,
    subDetailsError,
    onRequestRefund,
    requestingRefund,
    showRefundButton,
}) {
    if (loadingSubDetails) {
        return (
            <div id="subscription-details-section" className="mt-10 text-sm text-gray-500">
                Loading subscription details...
            </div>
        );
    }

    if (subDetailsError) {
        return (
            <div id="subscription-details-section" className="mt-10 text-sm text-red-500">
                {subDetailsError}
            </div>
        );
    }

    if (!subscriptionDetails) {
        return null;
    }

    const details = subscriptionDetails.subscription_details;
    const depositDamage = subscriptionDetails.deposit_and_damage;
    const totals = subscriptionDetails.subscription_totals;

    return (
        <div id="subscription-details-section" className="mt-10">
            <h2 className="text-lg font-semibold text-black mb-4">
                Subscription Details #{details?.id || selectedSubscriptionId}
            </h2>

            {/* Details Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden mb-8 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <tbody>
                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black w-1/3">Status</td>
                            <td className="py-3 px-4 text-gray-500">
                                {capitalize(details?.status)}
                            </td>
                        </tr>

                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">Start Date</td>
                            <td className="py-3 px-4 text-gray-500">
                                {formatDate(details?.start_date)}
                            </td>
                        </tr>

                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">Last Order Date</td>
                            <td className="py-3 px-4 text-gray-500">
                                {details?.last_order_date || "N/A"}
                            </td>
                        </tr>

                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">Next Payment Date</td>
                            <td className="py-3 px-4 text-gray-500">
                                {formatDate(details?.next_payment_date)}
                            </td>
                        </tr>

                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">End Date</td>
                            <td className="py-3 px-4 text-gray-500">
                                {formatDate(details?.end_date)}
                            </td>
                        </tr>

                        <tr>
                            <td className="py-3 px-4 font-medium text-black">Payment</td>
                            <td className="py-3 px-4 text-gray-500">
                                {details?.payment_method || "-"}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Deposit and Damage History */}
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-md font-semibold text-black">
                    Deposit And Damage History
                </h3>

                {showRefundButton && (
                    <button
                        onClick={onRequestRefund}
                        disabled={requestingRefund}
                        className="bg-black text-white px-4 py-1.5 rounded text-sm hover:bg-gray-900 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {requestingRefund ? "Requesting..." : "Request Refund"}
                    </button>
                )}
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden mb-8 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <tbody>
                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black w-1/3">
                                Deposit Amount
                            </td>
                            <td className="py-3 px-4 text-gray-500">
                                ₹{depositDamage?.deposit_amount || "-"}
                            </td>
                        </tr>

                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">
                                Damage And Other Charges
                            </td>
                            <td className="py-3 px-4 text-gray-500">
                                {depositDamage?.damage_charges
                                    ? `₹${formatCharges(depositDamage.damage_charges)}`
                                    : "No Damage Charges"}
                            </td>
                        </tr>

                        <tr>
                            <td className="py-3 px-4 font-medium text-black">
                                Remaining Amount
                            </td>
                            <td className="py-3 px-4 text-gray-500">
                                ₹{depositDamage?.remaining_amount ?? "-"}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Subscription Totals */}
            <h3 className="text-md font-semibold text-black mb-3">
                Subscription Totals
            </h3>

            <div className="border border-gray-200 rounded-lg overflow-hidden mb-10 bg-white shadow-sm">
                <table className="w-full text-sm">
                    <tbody>
                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">
                                {totals?.items?.[0]?.name || "-"} x{" "}
                                {totals?.items?.[0]?.quantity || 1}
                            </td>

                            <td className="py-3 px-4 text-gray-500 text-right">
                                ₹{totals?.items?.[0]?.total || "-"}
                            </td>
                        </tr>

                        <tr className="border-b">
                            <td className="py-3 px-4 font-medium text-black">Subtotal</td>
                            <td className="py-3 px-4 text-gray-500 text-right">
                                ₹{totals?.subtotal || "-"}
                            </td>
                        </tr>

                        <tr>
                            <td className="py-3 px-4 font-medium text-black">Total</td>
                            <td className="py-3 px-4 text-gray-500 text-right">
                                {stripHtml(decodeHtml(totals?.total || "-"))}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Related Orders */}
            <h3 className="text-md font-semibold text-black mb-3">
                Related Orders
            </h3>

            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="bg-gray-100 text-black">
                            <th className="py-3 px-4 text-left font-medium">Order</th>
                            <th className="py-3 px-4 text-center font-medium">Date</th>
                            <th className="py-3 px-4 text-center font-medium">Status</th>
                            <th className="py-3 px-4 text-center font-medium">Total</th>
                            <th className="py-3 px-4 text-center font-medium">Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        <tr className="border-b border-gray-200">
                            <td className="py-4 px-4 font-medium text-black">
                                #{details?.id || "-"}
                            </td>
                            <td className="py-4 px-4 text-center text-black">
                                {formatDate(details?.start_date)}
                            </td>
                            <td className="py-4 px-4 text-center text-black">
                                {capitalize(details?.status)}
                            </td>
                            <td className="py-4 px-4 text-center font-medium text-black">
                                ₹{totals?.subtotal || "-"}
                            </td>
                            <td className="py-4 px-4 text-center">
                                <button className="text-blue-400 border border-blue-300 rounded px-3 py-1 hover:bg-blue-50">
                                    View
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
}
